import * as React from 'react';
import styled from 'styled-components';
import ScrollHintArrow from '../ScrollHintArrow';

// ───────────────────────────────────────
// Styled Components
// ───────────────────────────────────────
const fadeFromBlack = `
  @keyframes headerFadeFromBlack {
    0% {
      opacity: 1;
    }
    22% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }
`;

const HeaderSection = styled.div`
  ${fadeFromBlack}
  position: relative;
  background-color: #000000;
  background-image: url('https://assets.volkswagen.com/is/image/volkswagenag/stage_gallo_desktop_final_2560x1440px?Zml0PWNyb3AlMkMxJndpZD0yNTYwJmhlaT0xNDQwJmZtdD1qcGVnJnFsdD03OSZiZmM9b2ZmJjRhNjQ=');
  background-size: cover;
  background-position: center;
  height: 100vh;
  width: 100%;
  overflow: hidden;
  transform: translateY(1px);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: #000000;
    pointer-events: none;
    z-index: 0;
    animation: headerFadeFromBlack 2.8s cubic-bezier(0.22, 1, 0.36, 1) forwards;
  }

  @media screen and (max-width: 860px) {
    background-position: 45% center;
    background-image: url('https://assets.volkswagen.com/is/image/volkswagenag/stage_gallo_mobile_final_1080x1440px?Zml0PWNyb3AlMkMxJndpZD0xMDgwJmhlaT0xOTIwJmZtdD1qcGVnJnFsdD03OSZiZmM9b2ZmJjMzNjU=');
  }
`;

const HeaderTitle = styled.div`
  position: relative;
  z-index: 1;
  height: 92vh;
  width: 86vw;
  margin-top: 26vh;
  margin-left: 4vw;
  transform: scale(0.64);
  transform-origin: left center;
  font-family: 'Futura Condensed Extra Bold', Futura, Arial, sans-serif;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: -0.04em;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  @media screen and (min-width: 356px) {
    transform: scale(0.92);
  }

  @media screen and (min-width: 560px) {
    width: min(66vw, 34rem);
    margin-left: 5vw;
    transform: scale(0.8);
  }

  @media screen and (min-width: 960px) {
    margin-top: 4vh;
    transform: scale(0.88);
  }

  @media screen and (min-width: 1280px) {
    width: min(46vw, 42rem);
    margin-top: 4vh;
    margin-left: 3.5vw;
    transform: scale(0.94);
  }

  @media screen and (min-width: 1600px) {
    transform: scale(1);
  }
`;

const TextLineWrapper = styled.div`
  position: relative;
  overflow: hidden;
`;

const TopTitle = styled(TextLineWrapper)`
  color: #ffffff;
  font-size: clamp(6rem, 13vw, 12rem);
  line-height: 1;
  padding-top: 0.15em;
  margin-bottom: -0.10em;

  @media screen and (max-width: 1050px) {
    font-size: clamp(4.5rem, 16vw, 8rem);
  }

  @media screen and (max-width: 560px) {
    font-size: 24vw;
  }
`;

const RotatingWord = styled.span`
  display: inline-block;
  transition: opacity 0.18s ease;
`;

const BottomTitleRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const TopTextRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: clamp(0.15rem, 0.45vw, 0.35rem);
`;

const SideNote = styled(TextLineWrapper)`
  color: #ffffff;
  font-size: clamp(1.4rem, 3vw, 2.6rem);
  line-height: 0.8;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transform: rotate(180deg);
  margin-top: 0.45rem;

  @media screen and (max-width: 1050px) {
    font-size: clamp(1.1rem, 4.2vw, 2.2rem);
  }

  @media screen and (max-width: 560px) {
    font-size: 7vw;
    margin-top: 0.2rem;
  }
`;

const MainStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  color: #e3000f;
  font-size: clamp(5.8rem, 11.5vw, 9rem);
  line-height: 0.8;
  margin-left: -0.03em;

  @media screen and (max-width: 1050px) {
    font-size: clamp(4.4rem, 15vw, 7.5rem);
  }

  @media screen and (max-width: 560px) {
    font-size: 23vw;
  }
`;

const UnoStack = styled(TextLineWrapper)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  color: #e3000f;
  font-size: clamp(7.2rem, 14vw, 11rem);
  line-height: 0.78;
  margin-left: -0.05em;

  @media screen and (max-width: 1050px) {
    font-size: clamp(5.4rem, 18vw, 8.8rem);
  }

  @media screen and (max-width: 560px) {
    font-size: 28vw;
  }
`;

const TOP_TITLE_WORDS = ['ICÓNICO', 'ETERNO', 'MÍTICO', 'GALLO', 'GTI'] as const;

interface HeaderUnoStackProps {
  className?: string;
}

export const HeaderUnoStack = ({ className }: HeaderUnoStackProps): React.ReactElement => (
  <UnoStack className={className}>Uno</UnoStack>
);

const ScrollHintArrowLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
`;

// ───────────────────────────────────────
// Header Component
// ───────────────────────────────────────
export const Header = (): React.ReactElement => {
  const headerSectionRef = React.useRef<HTMLDivElement | null>(null);
  const [topWordIndex, setTopWordIndex] = React.useState(TOP_TITLE_WORDS.length - 1);
  const [topWordOpacity, setTopWordOpacity] = React.useState(1);

  React.useEffect(() => {
    let fadeTimeoutId: number | undefined;

    const intervalId = window.setInterval(() => {
      setTopWordOpacity(0);

      fadeTimeoutId = window.setTimeout(() => {
        setTopWordIndex((currentIndex) => (currentIndex + 1) % TOP_TITLE_WORDS.length);
        setTopWordOpacity(1);
      }, 180);
    }, 1400);

    return () => {
      window.clearInterval(intervalId);
      if (fadeTimeoutId) {
        window.clearTimeout(fadeTimeoutId);
      }
    };
  }, []);

  return (
    <HeaderSection ref={headerSectionRef} className="header-section">
      <HeaderTitle className="header-title">
        <TopTitle>
          <RotatingWord style={{ opacity: topWordOpacity }}>{TOP_TITLE_WORDS[topWordIndex]}</RotatingWord>
        </TopTitle>

        <BottomTitleRow>
          <TopTextRow>
            <SideNote>Desde 1976</SideNote>

            <MainStack>
              <TextLineWrapper>Solo</TextLineWrapper>
              <TextLineWrapper>Hay</TextLineWrapper>
            </MainStack>
          </TopTextRow>

          <HeaderUnoStack />
        </BottomTitleRow>
      </HeaderTitle>

      <ScrollHintArrowLayer>
        <ScrollHintArrow trigger={headerSectionRef} targetSelector="#gallo-reveal-module" />
      </ScrollHintArrowLayer>
    </HeaderSection>
  );
};
